/**
 * PI-RADS v2.1 scoring types, reason constants, and utility functions.
 *
 * Reason wording is verbatim from PI-RADS v2.1 guidelines — do not modify
 * the label/value strings without clinical review.
 */

export type NumericScore = 1 | 2 | 3 | 4 | 5

export type PiradsZone = "AS" | "PZa" | "PZpl" | "PZpm" | "TZa" | "TZp" | "CZ"

export type PiradsLevel = "apex" | "mid-apex" | "mid" | "base-mid" | "base" | "all levels"

export type PiradsModality = "T2" | "DWI" | "DCE"

export type DceScore = "neg" | "pos"

export type ISUPGrade = "" | "unknown" | "no-cancer" | "1" | "2" | "3" | "4" | "5"

export interface PiradsReason {
  value: string
  label: string
  zone: PiradsZone | "PZ" | "TZ" | ""
  score: NumericScore | DceScore | ""
  modality: PiradsModality | ""
}

export type PiradsReasons = PiradsReason[]

export interface PIRADSScore {
  laterality: "left" | "right" | "middle" | ""
  level: PiradsLevel | ""
  zones: PiradsZone[]
  T2Score: NumericScore | ""
  T2Reason: string
  T2UpDownGrade: string
  DWIScore: NumericScore | ""
  DWIReason: string
  DWIUpDownGrade: string
  DCEScore: DceScore | ""
  DCEReason: string
  ISUPGrade: ISUPGrade
}

// ---------------------------------------------------------------------------
// T2 Reasons
// ---------------------------------------------------------------------------

export const t2Reasons: PiradsReasons = [
  // PZ - T2
  {
    value: "Uniform hyperintense signal intensity (normal)",
    label: "Uniform hyperintense signal intensity (normal)",
    zone: "PZ",
    score: 1,
    modality: "T2"
  },
  {
    value: "Linear or wedge-shaped hypointensity",
    label: "Linear or wedge-shaped hypointensity",
    zone: "PZ",
    score: 2,
    modality: "T2"
  },
  {
    value: "Diffuse mild hypointensity, usually indistinct margin",
    label: "Diffuse mild hypointensity, usually indistinct margin",
    zone: "PZ",
    score: 2,
    modality: "T2"
  },
  {
    value: "Heterogeneous signal intensity",
    label: "Heterogeneous signal intensity",
    zone: "PZ",
    score: 3,
    modality: "T2"
  },
  {
    value: "Non-circumscribed, rounded, moderate hypointensity",
    label: "Non-circumscribed, rounded, moderate hypointensity",
    zone: "PZ",
    score: 3,
    modality: "T2"
  },
  {
    value: "Includes others that do not qualify as 2, 4, or 5",
    label: "Includes others that do not qualify as 2, 4, or 5",
    zone: "PZ",
    score: 3,
    modality: "T2"
  },
  {
    value:
      "Circumscribed, homogenous moderate hypointense focus/mass confined to prostate and <1.5cm in greatest dimension",
    label:
      "Circumscribed, homogenous moderate hypointense focus/mass confined to prostate and <1.5cm in greatest dimension",
    zone: "PZ",
    score: 4,
    modality: "T2"
  },
  {
    value: "Same as 4, but >= 1.5cm in greatest dimension",
    label: "Same as 4, but >= 1.5cm in greatest dimension",
    zone: "PZ",
    score: 5,
    modality: "T2"
  },
  {
    value: "Definite extraprostatic extension/invasive behaviour",
    label: "Definite extraprostatic extension/invasive behaviour",
    zone: "PZ",
    score: 5,
    modality: "T2"
  },
  // CZ - T2
  {
    value: "Uniform hyperintense signal intensity (normal)",
    label: "Uniform hyperintense signal intensity (normal)",
    zone: "CZ",
    score: 1,
    modality: "T2"
  },
  {
    value: "Linear or wedge-shaped hypointensity",
    label: "Linear or wedge-shaped hypointensity",
    zone: "CZ",
    score: 2,
    modality: "T2"
  },
  {
    value: "Diffuse mild hypointensity, usually indistinct margin",
    label: "Diffuse mild hypointensity, usually indistinct margin",
    zone: "CZ",
    score: 2,
    modality: "T2"
  },
  {
    value: "Heterogeneous signal intensity",
    label: "Heterogeneous signal intensity",
    zone: "CZ",
    score: 3,
    modality: "T2"
  },
  {
    value: "Non-circumscribed, rounded, moderate hypointensity",
    label: "Non-circumscribed, rounded, moderate hypointensity",
    zone: "CZ",
    score: 3,
    modality: "T2"
  },
  {
    value: "Includes others that do not qualify as 2, 4, or 5",
    label: "Includes others that do not qualify as 2, 4, or 5",
    zone: "CZ",
    score: 3,
    modality: "T2"
  },
  {
    value: "Lenticular and <1.5cm in greatest dimension",
    label: "Lenticular and <1.5cm in greatest dimension",
    zone: "CZ",
    score: 4,
    modality: "T2"
  },
  {
    value: "Non-circumscribed, homogeneous, moderately hypointense, and <1.5cm in greatest dimension",
    label: "Non-circumscribed, homogeneous, moderately hypointense, and <1.5cm in greatest dimension",
    zone: "CZ",
    score: 4,
    modality: "T2"
  },
  {
    value: "Same as 4 (lenticular), but >= 1.5cm in greatest dimension",
    label: "Same as 4 (lenticular), but >= 1.5cm in greatest dimension",
    zone: "CZ",
    score: 5,
    modality: "T2"
  },
  {
    value: "Same as 4 (non-circumscribed, homogeneous, moderately hypointense), but >= 1.5cm in greatest dimension",
    label: "Same as 4 (non-circumscribed, homogeneous, moderately hypointense), but >= 1.5cm in greatest dimension",
    zone: "CZ",
    score: 5,
    modality: "T2"
  },
  {
    value: "Definite extraprostatic extension/ invasive behaviour",
    label: "Definite extraprostatic extension/ invasive behaviour",
    zone: "CZ",
    score: 5,
    modality: "T2"
  },
  // TZ - T2
  {
    value: "Normal appearing TZ",
    label: "Normal appearing TZ",
    zone: "TZ",
    score: 1,
    modality: "T2"
  },
  {
    value: 'Round, completely encapsulated nodule ("typical nodule")',
    label: 'Round, completely encapsulated nodule ("typical nodule")',
    zone: "TZ",
    score: 1,
    modality: "T2"
  },
  {
    value: "A mostly encapsulated nodule",
    label: "A mostly encapsulated nodule",
    zone: "TZ",
    score: 2,
    modality: "T2"
  },
  {
    value: 'Homogeneous circumscribed nodule without encapsulation ("atypical nodule")',
    label: 'Homogeneous circumscribed nodule without encapsulation ("atypical nodule")',
    zone: "TZ",
    score: 2,
    modality: "T2"
  },
  {
    value: "Homogeneous mildly hypointense area between nodules",
    label: "Homogeneous mildly hypointense area between nodules",
    zone: "TZ",
    score: 2,
    modality: "T2"
  },
  {
    value: "Heterogeneous signal intensity with obscured margins",
    label: "Heterogeneous signal intensity with obscured margins",
    zone: "TZ",
    score: 3,
    modality: "T2"
  },
  {
    value: "Includes others that do not qualify as 2, 4, or 5",
    label: "Includes others that do not qualify as 2, 4, or 5",
    zone: "TZ",
    score: 3,
    modality: "T2"
  },
  {
    value: "Lenticular or non-circumscribed, homogeneous, moderately hypointense, and <1.5cm in greatest dimension",
    label: "Lenticular or non-circumscribed, homogeneous, moderately hypointense, and <1.5cm in greatest dimension",
    zone: "TZ",
    score: 4,
    modality: "T2"
  },
  {
    value: "Same as 4 (non-circumscribed, homogeneous, moderately hypointense), but >= 1.5cm in greatest dimension",
    label: "Same as 4 (non-circumscribed, homogeneous, moderately hypointense), but >= 1.5cm in greatest dimension",
    zone: "TZ",
    score: 5,
    modality: "T2"
  },
  {
    value: "Definite extraprostatic extension/ invasive behaviour",
    label: "Definite extraprostatic extension/ invasive behaviour",
    zone: "TZ",
    score: 5,
    modality: "T2"
  },
  // AS - T2
  {
    value: "Normal appearing TZ",
    label: "Normal appearing TZ",
    zone: "AS",
    score: 1,
    modality: "T2"
  },
  {
    value: 'Round, completely encapsulated nodule ("typical nodule")',
    label: 'Round, completely encapsulated nodule ("typical nodule")',
    zone: "AS",
    score: 1,
    modality: "T2"
  },
  {
    value: "A mostly encapsulated nodule",
    label: "A mostly encapsulated nodule",
    zone: "AS",
    score: 2,
    modality: "T2"
  },
  {
    value: 'Homogeneous circumscribed nodule without encapsulation ("atypical nodule")',
    label: 'Homogeneous circumscribed nodule without encapsulation ("atypical nodule")',
    zone: "AS",
    score: 2,
    modality: "T2"
  },
  {
    value: "Homogeneous mildly hypointense area between nodules",
    label: "Homogeneous mildly hypointense area between nodules",
    zone: "AS",
    score: 2,
    modality: "T2"
  },
  {
    value: "Heterogeneous signal intensity with obscured margins",
    label: "Heterogeneous signal intensity with obscured margins",
    zone: "AS",
    score: 3,
    modality: "T2"
  },
  {
    value: "Includes others that do not qualify as 2, 4, or 5",
    label: "Includes others that do not qualify as 2, 4, or 5",
    zone: "AS",
    score: 3,
    modality: "T2"
  },
  {
    value: "Lenticular and <1.5cm in greatest dimension",
    label: "Lenticular and <1.5cm in greatest dimension",
    zone: "AS",
    score: 4,
    modality: "T2"
  },
  {
    value: "Non-circumscribed, homogeneous, moderately hypointense, and <1.5cm in greatest dimension",
    label: "Non-circumscribed, homogeneous, moderately hypointense, and <1.5cm in greatest dimension",
    zone: "AS",
    score: 4,
    modality: "T2"
  },
  {
    value: "Same as 4 (lenticular), but >= 1.5cm in greatest dimension",
    label: "Same as 4 (lenticular), but >= 1.5cm in greatest dimension",
    zone: "AS",
    score: 5,
    modality: "T2"
  },
  {
    value: "Same as 4 (non-circumscribed, homogeneous, moderately hypointense), but >= 1.5cm in greatest dimension",
    label: "Same as 4 (non-circumscribed, homogeneous, moderately hypointense), but >= 1.5cm in greatest dimension",
    zone: "AS",
    score: 5,
    modality: "T2"
  },
  {
    value: "Definite extraprostatic extension/ invasive behaviour",
    label: "Definite extraprostatic extension/ invasive behaviour",
    zone: "AS",
    score: 5,
    modality: "T2"
  }
]

// ---------------------------------------------------------------------------
// DWI Reasons
// ---------------------------------------------------------------------------

export const dwiReasons: PiradsReasons = [
  // CZ - DWI
  {
    value: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    label: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    zone: "CZ",
    score: 1,
    modality: "DWI"
  },
  {
    value: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    label: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    zone: "CZ",
    score: 2,
    modality: "DWI"
  },
  {
    value:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    label:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    zone: "CZ",
    score: 3,
    modality: "DWI"
  },
  {
    value:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    label:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    zone: "CZ",
    score: 4,
    modality: "DWI"
  },
  {
    value: "Same as 4, but ≥1.5cm in greatest dimension",
    label: "Same as 4, but ≥1.5cm in greatest dimension",
    zone: "CZ",
    score: 5,
    modality: "DWI"
  },
  {
    value: "Definite extraprostatic extension/invasive behavior",
    label: "Definite extraprostatic extension/invasive behavior",
    zone: "CZ",
    score: 5,
    modality: "DWI"
  },
  // PZ - DWI
  {
    value: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    label: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    zone: "PZ",
    score: 1,
    modality: "DWI"
  },
  {
    value: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    label: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    zone: "PZ",
    score: 2,
    modality: "DWI"
  },
  {
    value:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    label:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    zone: "PZ",
    score: 3,
    modality: "DWI"
  },
  {
    value:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    label:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    zone: "PZ",
    score: 4,
    modality: "DWI"
  },
  {
    value: "Same as 4, but ≥1.5cm in greatest dimension",
    label: "Same as 4, but ≥1.5cm in greatest dimension",
    zone: "PZ",
    score: 5,
    modality: "DWI"
  },
  {
    value: "Definite extraprostatic extension/invasive behavior",
    label: "Definite extraprostatic extension/invasive behavior",
    zone: "PZ",
    score: 5,
    modality: "DWI"
  },
  // TZ - DWI
  {
    value: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    label: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    zone: "TZ",
    score: 1,
    modality: "DWI"
  },
  {
    value: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    label: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    zone: "TZ",
    score: 2,
    modality: "DWI"
  },
  {
    value:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    label:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    zone: "TZ",
    score: 3,
    modality: "DWI"
  },
  {
    value:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    label:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    zone: "TZ",
    score: 4,
    modality: "DWI"
  },
  {
    value: "Same as 4, but ≥1.5cm in greatest dimension",
    label: "Same as 4, but ≥1.5cm in greatest dimension",
    zone: "TZ",
    score: 5,
    modality: "DWI"
  },
  {
    value: "Definite extraprostatic extension/invasive behavior",
    label: "Definite extraprostatic extension/invasive behavior",
    zone: "TZ",
    score: 5,
    modality: "DWI"
  },
  // AS - DWI
  {
    value: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    label: "No abnormality (i.e., normal) on ADC and high b-value DWI",
    zone: "AS",
    score: 1,
    modality: "DWI"
  },
  {
    value: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    label: "Linear/wedge shaped hypointense on ADC and/or linear/wedge shaped hyperintense on high b-value DWI",
    zone: "AS",
    score: 2,
    modality: "DWI"
  },
  {
    value:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    label:
      "Focal (discrete and different from the background) hypointense on ADC and/or focal hyperintense on high b-value DWI; may be markedly hypointense on ADC or markedly hyperintense on high b-value DWI, but not both",
    zone: "AS",
    score: 3,
    modality: "DWI"
  },
  {
    value:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    label:
      "Focal markedly hypontense on ADC and markedly hyperintense on high b-value DWI; <1.5cm in greatest dimension",
    zone: "AS",
    score: 4,
    modality: "DWI"
  },
  {
    value: "Same as 4, but ≥1.5cm in greatest dimension",
    label: "Same as 4, but ≥1.5cm in greatest dimension",
    zone: "AS",
    score: 5,
    modality: "DWI"
  },
  {
    value: "Definite extraprostatic extension/invasive behavior",
    label: "Definite extraprostatic extension/invasive behavior",
    zone: "AS",
    score: 5,
    modality: "DWI"
  }
]

// ---------------------------------------------------------------------------
// DCE Reasons
// ---------------------------------------------------------------------------

export const dceReasons: PiradsReasons = [
  {
    value:
      "no early or contemporaneous enhancement; or diffuse multifocal enhancement NOT corresponding to a focal finding on T2W and/or DWI or focal enhancement corresponding to a lesion demonstrating features of BPH on T2WI (including features of extruded BPH in the PZ)",
    label:
      "no early or contemporaneous enhancement; or diffuse multifocal enhancement NOT corresponding to a focal finding on T2W and/or DWI or focal enhancement corresponding to a lesion demonstrating features of BPH on T2WI (including features of extruded BPH in the PZ)",
    zone: "",
    score: "neg",
    modality: "DCE"
  },
  {
    value:
      "focal, and; earlier than or contemporaneously with enhancement of adjacent normal prostatic tissues, and; corresponds to suspicious finding on T2W and/or DWI",
    label:
      "focal, and; earlier than or contemporaneously with enhancement of adjacent normal prostatic tissues, and; corresponds to suspicious finding on T2W and/or DWI",
    zone: "",
    score: "pos",
    modality: "DCE"
  }
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function matchesZone(reasonZone: string, searchZone: string): boolean {
  if (reasonZone === searchZone) return true
  if (reasonZone === "" && searchZone === "") return true
  if (reasonZone === "PZ" && ["PZa", "PZpl", "PZpm"].includes(searchZone)) return true
  if (reasonZone === "TZ" && ["TZa", "TZp"].includes(searchZone)) return true
  return false
}

export function getPiradsReasons(
  zone: PiradsZone | string,
  score: NumericScore | DceScore | string,
  modality: PiradsModality | string,
  piradsReasons: PiradsReasons
): string[] {
  return piradsReasons
    .filter((reason) => matchesZone(reason.zone, zone) && reason.score === score && reason.modality === modality)
    .map((reason) => reason.value)
}

// ---------------------------------------------------------------------------
// Utility functions (ported from old pirads-utils.ts)
// ---------------------------------------------------------------------------

export function createDefaultPiradsScore(): PIRADSScore {
  return {
    laterality: "",
    level: "",
    zones: [],
    T2Score: "",
    T2Reason: "",
    T2UpDownGrade: "",
    DWIScore: "",
    DWIReason: "",
    DWIUpDownGrade: "",
    DCEScore: "",
    DCEReason: "",
    ISUPGrade: ""
  }
}

export type Modality = "t2" | "dwi" | "dce"

export const SCORE_FIELD_MAP = {
  t2: "T2Score",
  dwi: "DWIScore",
  dce: "DCEScore"
} as const

export const REASON_FIELD_MAP = {
  t2: "T2Reason",
  dwi: "DWIReason",
  dce: "DCEReason"
} as const

export const POTENTIAL_FIELD_MAP = {
  t2: "T2UpDownGrade",
  dwi: "DWIUpDownGrade",
  dce: ""
} as const

export function getScoreOptions(modality: Modality) {
  if (modality === "dce") {
    return [
      { value: "", label: "*" },
      { value: "neg", label: "Neg" },
      { value: "pos", label: "Pos" }
    ]
  }
  return [
    { value: "", label: "*" },
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" }
  ]
}

export function convertScoreValue(modality: Modality, value: string): NumericScore | DceScore | "" {
  if (modality === "dce") {
    return value as DceScore | ""
  }
  if (value && ["1", "2", "3", "4", "5"].includes(value)) {
    return Number.parseInt(value) as NumericScore
  }
  return ""
}

export function updateZonesList(currentZones: PiradsZone[], zoneIndex: number, zone: PiradsZone | ""): PiradsZone[] {
  const newZones = [...currentZones]
  if (zone === "") {
    newZones.splice(zoneIndex, 1)
  } else {
    newZones[zoneIndex] = zone
  }
  return newZones.filter((z): z is PiradsZone => Boolean(z))
}

/** All EAV field keys persisted for a single PI-RADS annotation. */
export const PIRADS_TEXT_FIELDS = [
  "laterality",
  "level",
  "zones",
  "t2_reason",
  "t2_up_down_grade",
  "dwi_reason",
  "dwi_up_down_grade",
  "dce_score",
  "dce_reason",
  "isup_grade"
] as const

export const PIRADS_INT_FIELDS = ["t2_score", "dwi_score"] as const

export const PIRADS_ALL_FIELDS: readonly string[] = [...PIRADS_TEXT_FIELDS, ...PIRADS_INT_FIELDS]
